/**
 * Map Layer Preference Storage Utilities
 *
 * Manages localStorage persistence for user's preferred map visualization layer.
 * Handles errors gracefully and provides fallback defaults.
 *
 * @module utils/mapStorage
 */

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * localStorage key for storing map layer preference
 * @constant {string}
 */
const STORAGE_KEY = 'voo_map_layer_preference';

/**
 * Valid map layer types
 * @constant {string[]}
 */
const VALID_LAYERS = ['satellite', 'hybrid', 'streets'];

/**
 * Default layer type when no preference is set
 * @constant {string}
 */
const DEFAULT_LAYER = 'hybrid';

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Validate if a value is a valid layer type
 *
 * @param {string} value - Value to validate
 * @returns {boolean} True if value is a valid layer type
 */
function isValidLayer(value) {
  return VALID_LAYERS.includes(value);
}

/**
 * Get saved map layer preference from localStorage
 *
 * Attempts to read the user's preferred map layer from localStorage.
 * Returns null if:
 * - localStorage is unavailable (private browsing, cookies disabled)
 * - No preference has been saved
 * - Saved value is invalid or corrupted
 *
 * @returns {string|null} Saved preference ('satellite' | 'hybrid' | 'streets') or null
 *
 * @example
 * const preference = getLayerPreference();
 * if (preference) {
 *   console.log('User prefers:', preference);
 * } else {
 *   console.log('Using default layer');
 * }
 */
export function getLayerPreference() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);

    // Check if value exists
    if (!stored) {
      return null;
    }

    // Validate stored value
    if (isValidLayer(stored)) {
      return stored;
    }

    // Invalid value found, clear it
    console.warn('Invalid map layer preference in storage:', stored);
    localStorage.removeItem(STORAGE_KEY);
    return null;
  } catch (error) {
    // localStorage unavailable (private mode, cookies disabled, etc.)
    console.warn('Failed to read map layer preference from localStorage:', error.message);
    return null;
  }
}

/**
 * Save map layer preference to localStorage
 *
 * Attempts to save the user's preferred map layer to localStorage.
 * Fails gracefully if localStorage is unavailable.
 *
 * @param {string} layer - Layer preference to save ('satellite' | 'hybrid' | 'streets')
 * @returns {boolean} True if save was successful, false otherwise
 *
 * @example
 * const success = setLayerPreference('satellite');
 * if (success) {
 *   console.log('Preference saved');
 * } else {
 *   console.warn('Failed to save preference');
 * }
 */
export function setLayerPreference(layer) {
  try {
    // Validate input
    if (!isValidLayer(layer)) {
      console.warn('Attempted to save invalid layer preference:', layer);
      return false;
    }

    // Attempt to save
    localStorage.setItem(STORAGE_KEY, layer);
    return true;
  } catch (error) {
    // Handle various localStorage errors
    if (error.name === 'QuotaExceededError') {
      console.warn('localStorage quota exceeded, unable to save map layer preference');
    } else if (error.name === 'SecurityError') {
      console.warn('localStorage access denied (private browsing mode)');
    } else {
      console.warn('Failed to save map layer preference:', error.message);
    }
    return false;
  }
}

/**
 * Clear saved map layer preference from localStorage
 *
 * Removes the stored preference, causing the app to revert to default layer.
 * Useful for testing or resetting user preferences.
 *
 * @returns {boolean} True if clear was successful, false otherwise
 *
 * @example
 * clearLayerPreference();
 * // Next page load will use default 'streets' layer
 */
export function clearLayerPreference() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (error) {
    console.warn('Failed to clear map layer preference:', error.message);
    return false;
  }
}

/**
 * Get initial layer preference with fallback to default
 *
 * Convenience function that returns the saved preference or the default layer.
 * This is the recommended way to initialize the layer state on component mount.
 *
 * @returns {string} Layer preference ('satellite' | 'hybrid' | 'streets')
 *
 * @example
 * const [activeLayer, setActiveLayer] = useState(() => getInitialLayerPreference());
 */
export function getInitialLayerPreference() {
  return getLayerPreference() || DEFAULT_LAYER;
}

// =============================================================================
// REACT HOOK (Optional)
// =============================================================================

/**
 * Note: This file provides utility functions that work with any state management.
 * For projects using React hooks, you can create a custom hook like this:
 *
 * ```javascript
 * import { useState, useCallback } from 'react';
 * import { getLayerPreference, setLayerPreference } from '../utils/mapStorage';
 *
 * function useMapLayerPreference() {
 *   const [preference, setPreference] = useState(() => getInitialLayerPreference());
 *
 *   const updatePreference = useCallback((layer) => {
 *     setLayerPreference(layer);
 *     setPreference(layer);
 *   }, []);
 *
 *   return [preference, updatePreference];
 * }
 * ```
 */
